import { useState, useMemo, useRef, useCallback, useId, cloneElement, isValidElement } from "react";
import { KacBadge } from "../components/KacMark";
import { TIER_DESCRIPTIONS, signupTiersFor, tierLabel } from "../utils/tierDescriptions";
import {
  blankApplication,
  validateApplication,
  submitApplication,
  suggestedClassification,
  ageFromDob,
  slotsFor,
  AVAILABILITY_DAYS,
} from "../utils/applications";

// Public, no-login membership sign-up form reached at "/?join". Submits to the
// unauthenticated POST /api.php?apply endpoint, which appends to the
// applications file — nothing here can read or change the live roster. A coach
// reviews and approves each submission in the Sign-ups tab.
export default function JoinView() {
  const [draft, setDraft] = useState(blankApplication);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [reference, setReference] = useState(null);
  const formTop = useRef(null);

  const isJunior = draft.classification === "Junior";
  const age = useMemo(() => ageFromDob(draft.dob), [draft.dob]);
  const tiers = useMemo(() => signupTiersFor(draft.classification), [draft.classification]);
  const slots = useMemo(() => slotsFor(draft.classification), [draft.classification]);

  // Errors are keyed by field and the summary banner keys off the object being
  // non-empty, so clearing one has to delete the key rather than blank it.
  const clearErrors = useCallback((...fields) => {
    setErrors(prev => {
      if (!fields.some(f => f in prev)) return prev;
      const next = { ...prev };
      fields.forEach(f => delete next[f]);
      return next;
    });
  }, []);

  function set(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
    clearErrors(field);
    setSubmitError(null);
  }

  // Entering a date of birth pre-selects adult vs junior (the member can still
  // change it) and clears a tier that belongs to the other list.
  function setDob(value) {
    const suggested = suggestedClassification(value);
    setDraft(prev => {
      const classification = prev.classification || suggested;
      const allowed = signupTiersFor(classification);
      return {
        ...prev,
        dob: value,
        classification,
        tier: allowed.includes(prev.tier) ? prev.tier : "",
      };
    });
    clearErrors("dob");
  }

  // Switching adult/junior invalidates both the tier list and the time slots,
  // so drop any selections that no longer exist.
  function setClassification(value) {
    setDraft(prev => {
      const allowedSlots = slotsFor(value);
      return {
        ...prev,
        classification: value,
        tier: signupTiersFor(value).includes(prev.tier) ? prev.tier : "",
        availability: prev.availability.filter(k => allowedSlots.includes(k.split("|")[1])),
      };
    });
    clearErrors("classification", "tier");
  }

  function toggleAvailability(day, time) {
    const key = `${day}|${time}`;
    setDraft(prev => {
      const s = new Set(prev.availability);
      if (s.has(key)) s.delete(key);
      else s.add(key);
      return { ...prev, availability: [...s] };
    });
    clearErrors("availability");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const found = validateApplication(draft);
    const cleaned = Object.fromEntries(Object.entries(found).filter(([, v]) => v));
    setErrors(cleaned);
    if (Object.keys(cleaned).length > 0) {
      // Put the first problem in view rather than leaving people hunting.
      const first = document.querySelector("[data-error='true']");
      (first || formTop.current)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const ref = await submitApplication(draft);
      setReference(ref);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(
        err?.message === "Submission failed"
          ? "We couldn't save your application. Please check your connection and try again."
          : err.message
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Reset for a family signing up a second child — keep the contact and
  // guardian details, clear everything specific to the person.
  function startAnother() {
    setDraft(prev => ({
      ...blankApplication(),
      email: prev.email,
      phone: prev.phone,
      address: prev.address,
      contactMethod: prev.contactMethod,
      guardianName: prev.guardianName,
      guardianRelationship: prev.guardianRelationship,
      guardianPhone: prev.guardianPhone,
      guardianEmail: prev.guardianEmail,
      emergencyName: prev.emergencyName,
      emergencyPhone: prev.emergencyPhone,
    }));
    setErrors({});
    setReference(null);
    window.scrollTo({ top: 0 });
  }

  if (reference) {
    return <ThankYou reference={reference} onAnother={startAnother} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto" ref={formTop}>
        {/* Header */}
        <div className="text-center mb-6 mt-2">
          <KacBadge className="mb-4" size="w-16 h-16 text-3xl rounded-2xl" />
          <p className="font-display text-brand-blue text-lg tracking-wide">Kings Athletic Club</p>
          <h1 className="text-3xl font-display text-brand-blue">Join the club</h1>
          <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
            Tell us a little about yourself and when you can play. A coach will review
            your details and be in touch to confirm your group.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* --- Member details --- */}
          <Card title="Member details" step={1}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First name" required error={errors.firstName}>
                <Input
                  value={draft.firstName}
                  onChange={v => set("firstName", v)}
                  autoComplete="given-name"
                  invalid={!!errors.firstName}
                />
              </Field>
              <Field label="Last name" required error={errors.lastName}>
                <Input
                  value={draft.lastName}
                  onChange={v => set("lastName", v)}
                  autoComplete="family-name"
                  invalid={!!errors.lastName}
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Date of birth"
                required
                error={errors.dob}
                hint={age !== null ? `Age ${age}` : undefined}
              >
                <Input
                  type="date"
                  value={draft.dob}
                  onChange={setDob}
                  autoComplete="bday"
                  invalid={!!errors.dob}
                />
              </Field>
              <Field label="Gender" required error={errors.gender}>
                <Select
                  value={draft.gender}
                  onChange={v => set("gender", v)}
                  invalid={!!errors.gender}
                  options={[
                    { value: "", label: "Select…" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                    { value: "Prefer not to say", label: "Prefer not to say" },
                  ]}
                />
              </Field>
            </div>

            <Field
              label="Is this member an adult or a junior?"
              required
              group
              error={errors.classification}
              hint="Juniors are under 18 and train in the after-school sessions."
            >
              <div className="grid grid-cols-2 gap-3">
                {["Adult", "Junior"].map(c => (
                  <ChoiceButton
                    key={c}
                    selected={draft.classification === c}
                    onClick={() => setClassification(c)}
                  >
                    {c}
                  </ChoiceButton>
                ))}
              </div>
            </Field>
          </Card>

          {/* --- Contact --- */}
          <Card title="Contact details" step={2}>
            <p className="text-xs text-gray-500 -mt-1">
              {isJunior
                ? "The best contact for club news and schedule changes — usually a parent's."
                : "How we'll reach you about your group and any schedule changes."}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email" required error={errors.email}>
                <Input
                  type="email"
                  value={draft.email}
                  onChange={v => set("email", v)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  invalid={!!errors.email}
                />
              </Field>
              <Field label="Mobile number" required error={errors.phone}>
                <Input
                  type="tel"
                  value={draft.phone}
                  onChange={v => set("phone", v)}
                  placeholder="+1 555 123 4567"
                  autoComplete="tel"
                  invalid={!!errors.phone}
                />
              </Field>
            </div>
            <Field label="Home address" optional>
              <Input
                value={draft.address}
                onChange={v => set("address", v)}
                placeholder="Street, city, postcode"
                autoComplete="street-address"
              />
            </Field>
            <Field label="Preferred way to be contacted" optional>
              <Select
                value={draft.contactMethod}
                onChange={v => set("contactMethod", v)}
                options={[
                  { value: "", label: "No preference" },
                  { value: "Email", label: "Email" },
                  { value: "Phone call", label: "Phone call" },
                  { value: "Text / WhatsApp", label: "Text / WhatsApp" },
                ]}
              />
            </Field>
          </Card>

          {/* --- Guardian & emergency (juniors only) --- */}
          {isJunior && (
            <Card title="Parent / guardian & emergency contact" step={3}>
              <p className="text-xs text-gray-500 -mt-1">
                Required for all members under 18.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Parent / guardian name" required error={errors.guardianName}>
                  <Input
                    value={draft.guardianName}
                    onChange={v => set("guardianName", v)}
                    invalid={!!errors.guardianName}
                  />
                </Field>
                <Field label="Relationship to member" required error={errors.guardianRelationship}>
                  <Select
                    value={draft.guardianRelationship}
                    onChange={v => set("guardianRelationship", v)}
                    invalid={!!errors.guardianRelationship}
                    options={[
                      { value: "", label: "Select…" },
                      { value: "Mother", label: "Mother" },
                      { value: "Father", label: "Father" },
                      { value: "Guardian", label: "Guardian" },
                      { value: "Grandparent", label: "Grandparent" },
                      { value: "Other", label: "Other" },
                    ]}
                  />
                </Field>
                <Field label="Parent / guardian mobile" required error={errors.guardianPhone}>
                  <Input
                    type="tel"
                    value={draft.guardianPhone}
                    onChange={v => set("guardianPhone", v)}
                    invalid={!!errors.guardianPhone}
                  />
                </Field>
                <Field label="Parent / guardian email" required error={errors.guardianEmail}>
                  <Input
                    type="email"
                    value={draft.guardianEmail}
                    onChange={v => set("guardianEmail", v)}
                    invalid={!!errors.guardianEmail}
                  />
                </Field>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 mt-1">
                  Emergency contact — someone else we can call
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" required error={errors.emergencyName}>
                    <Input
                      value={draft.emergencyName}
                      onChange={v => set("emergencyName", v)}
                      invalid={!!errors.emergencyName}
                    />
                  </Field>
                  <Field label="Phone number" required error={errors.emergencyPhone}>
                    <Input
                      type="tel"
                      value={draft.emergencyPhone}
                      onChange={v => set("emergencyPhone", v)}
                      invalid={!!errors.emergencyPhone}
                    />
                  </Field>
                </div>
              </div>
            </Card>
          )}

          {/* --- Level --- */}
          <Card title="Playing level" step={isJunior ? 4 : 3}>
            {!draft.classification ? (
              <p className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                Choose adult or junior above and we'll show you the right groups.
              </p>
            ) : (
              <Field
                label="Which group best describes the member?"
                required
                group
                error={errors.tier}
                hint="Pick the closest match — a coach confirms the final group after an assessment."
              >
                <div className="space-y-2">
                  {tiers.map(t => (
                    <TierOption
                      key={t}
                      selected={draft.tier === t}
                      onClick={() => set("tier", t)}
                      label={tierLabel(t)}
                      description={TIER_DESCRIPTIONS[t]}
                    />
                  ))}
                </div>
              </Field>
            )}

            <Field
              label="Anything else about their level?"
              optional
              hint="Years played, current rating, previous club or coach."
            >
              <Input
                value={draft.skillLevel}
                onChange={v => set("skillLevel", v)}
                placeholder="e.g. Played 3 years, USTA 3.5"
              />
            </Field>
          </Card>

          {/* --- Availability --- */}
          <Card title="Availability" step={isJunior ? 5 : 4}>
            <Field
              label="When could the member attend?"
              required
              group
              error={errors.availability}
              hint={
                isJunior
                  ? "Junior sessions run after school, Monday to Friday. Tick every time that works — the more you tick, the easier you are to place."
                  : "Tick every time that works, Monday to Friday. The more you tick, the easier you are to place."
              }
            >
              <div className="space-y-3">
                {AVAILABILITY_DAYS.map(day => (
                  <div key={day}>
                    <div className="text-xs font-semibold text-gray-500 mb-1.5">{day}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {slots.map(time => {
                        const checked = draft.availability.includes(`${day}|${time}`);
                        return (
                          <label
                            key={time}
                            className={`text-xs px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors select-none ${
                              checked
                                ? "bg-blue-100 border-blue-300 text-blue-800 font-medium"
                                : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleAvailability(day, time)}
                              className="sr-only"
                            />
                            {time}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </Field>
            <p className="text-xs text-gray-500">
              {draft.availability.length} time{draft.availability.length === 1 ? "" : "s"} selected
            </p>
          </Card>

          {/* --- Anything else --- */}
          <Card title="Anything else" step={isJunior ? 6 : 5}>
            <Field
              label="Anyone you'd like to be grouped with?"
              optional
              hint="A friend, sibling or regular hitting partner already at the club."
            >
              <Input
                value={draft.preferredPartners}
                onChange={v => set("preferredPartners", v)}
                placeholder="e.g. John Smith, Jane Doe"
              />
            </Field>

            <label className="flex items-start gap-3 cursor-pointer bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <input
                type="checkbox"
                checked={draft.privateInterest}
                onChange={e => set("privateInterest", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
              />
              <span className="text-sm text-gray-700">
                I'm interested in private lessons
                <span className="block text-xs text-gray-500">
                  Weekend afternoons. A coach will follow up with times and pricing.
                </span>
              </span>
            </label>

            <Field label="Notes for the coaches" optional>
              <textarea
                value={draft.notes}
                onChange={e => set("notes", e.target.value)}
                rows={3}
                placeholder="Injuries, goals, anything we should know…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
              />
            </Field>
          </Card>

          {submitError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              {submitError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              Please fill in the highlighted fields above.
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold py-3 rounded-xl text-sm transition-colors shadow-sm"
          >
            {submitting ? "Sending…" : "Submit application"}
          </button>
          <p className="text-center text-xs text-gray-400 pb-8">
            We use these details only to organise your sessions and contact you about the club.
          </p>
        </form>
      </div>
    </div>
  );
}

function ThankYou({ reference, onAnother }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <KacBadge className="mb-4" size="w-16 h-16 text-3xl rounded-2xl" />
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-4xl mb-3">🎾</div>
          <h1 className="text-2xl font-display text-brand-blue mb-2">Application received</h1>
          <p className="text-sm text-gray-600 mb-5">
            Thanks for signing up. A coach will review your details and contact you to
            confirm your group and start date.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6">
            <div className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-1">
              Your reference
            </div>
            <div className="text-lg font-mono font-semibold text-blue-800">{reference}</div>
            <div className="text-xs text-blue-600 mt-1">Quote this if you get in touch.</div>
          </div>
          <button
            onClick={onAnother}
            className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg transition-colors"
          >
            Sign up another family member
          </button>
        </div>
      </div>
    </div>
  );
}

/* --- Small presentational pieces ---------------------------------------- */

function Card({ title, step, children }) {
  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 space-y-4">
      <h2 className="flex items-center gap-2.5 text-base font-semibold text-gray-800">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">
          {step}
        </span>
        {title}
      </h2>
      {children}
    </section>
  );
}

// Wires the label, hint and error message to the control it wraps, so tapping
// the label focuses the input and screen readers announce the error. The
// control is cloned to receive the generated id — that keeps every call site
// free of manual id bookkeeping. Groups of buttons/checkboxes have no single
// focusable control, so they fall back to a plain label.
function Field({ label, required, optional, hint, error, group, children }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const control = !group && isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
      })
    : children;

  return (
    <div data-error={error ? "true" : undefined}>
      <Label htmlFor={group ? undefined : id} group={group} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>}
        {required && <span className="sr-only"> (required)</span>}
        {optional && <span className="text-gray-400 font-normal text-xs ml-1.5">(optional)</span>}
      </Label>
      {hint && <p id={hintId} className="text-xs text-gray-500 mb-2">{hint}</p>}
      {control}
      {error && (
        <p id={errorId} className="text-xs text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}

// A real <label> when there's one control to point at; a plain heading when the
// "field" is a group of buttons or checkboxes, which a label can't target.
function Label({ htmlFor, group, className, children }) {
  if (group) {
    return (
      <div className={className} role="heading" aria-level="3">
        {children}
      </div>
    );
  }
  return (
    <label htmlFor={htmlFor} className={className}>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", invalid, ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent ${
        invalid
          ? "border-red-300 bg-red-50 focus:ring-red-400"
          : "border-gray-300 focus:ring-blue-400"
      }`}
      {...rest}
    />
  );
}

function Select({ value, onChange, options, invalid, ...rest }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      {...rest}
      className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:border-transparent ${
        invalid
          ? "border-red-300 bg-red-50 focus:ring-red-400"
          : "border-gray-300 focus:ring-blue-400"
      }`}
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ChoiceButton({ selected, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${
        selected
          ? "bg-blue-600 border-blue-600 text-white"
          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

function TierOption({ selected, onClick, label, description }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
        selected
          ? "bg-blue-50 border-blue-400 ring-1 ring-blue-300"
          : "bg-white border-gray-200 hover:bg-gray-50"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border-2 ${
            selected ? "border-blue-600 bg-blue-600 ring-2 ring-white ring-inset" : "border-gray-300"
          }`}
        />
        <span>
          <span className={`block text-sm font-medium ${selected ? "text-blue-900" : "text-gray-800"}`}>
            {label}
          </span>
          <span className="block text-xs text-gray-500 mt-0.5">{description}</span>
        </span>
      </div>
    </button>
  );
}
